import { ReactNode } from "react";
import "@fontsource/quicksand/400.css";
import "@fontsource/quicksand/500.css";
import "@fontsource/quicksand/600.css";
import "@fontsource/quicksand/700.css";
import {
  Box,
  Button,
  Container,
  CssBaseline,
  Stack,
  ThemeProvider,
  useMediaQuery,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../shared/auth/AuthContext";
import { NavBar } from "../components/NavBar";
import { theme } from "../../shared/util/Theme";
import { UserMenu } from "../components/UserMenu";
import { Footer } from "../components/Footer";
import { brand, isReallyChitty } from "../../shared/util/branding";

import prettyChittyLogo from "../../assets/full_logo.png";
import reallyChittyLogo from "../../assets/reallychitty_logo.svg";

const logo = isReallyChitty() ? reallyChittyLogo : prettyChittyLogo;

export function NavLayout({ children }: { children: ReactNode | ReactNode[] }) {
  const { user, loading } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}
      >
        {/* Header area */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1100,
            background: theme.palette.background.paper,
            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          }}
        >
          <Container
            maxWidth={"md"}
            sx={{
              WebkitTouchCallout: "none",
            }}
          >
            <Stack
              direction={"row"}
              alignItems="center"
              justifyContent="space-between"
              sx={{ py: 2, minHeight: 70 }}
            >
              <Link to="/" style={{ display: "flex", alignItems: "center" }}>
                <img src={logo} alt={`${brand.name} Logo`} height={50} />
              </Link>

              <NavBar user={user} loading={loading} />

              {!isMobile && (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {user && <UserMenu user={user} />}
                  {!user && !loading && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => navigate("/login")}
                      sx={{
                        borderColor: theme.palette.secondary.main,
                        color: theme.palette.text.primary,
                        fontWeight: 600,
                        "&:hover": {
                          borderColor: theme.palette.secondary.light,
                          background: "rgba(140, 0, 255, 0.1)",
                        },
                      }}
                    >
                      Login
                    </Button>
                  )}
                </Box>
              )}
            </Stack>
          </Container>
        </Box>

        {/* Main content area */}
        <Box sx={{ flex: 1 }}>
          <Container
            maxWidth={"md"}
            sx={{
              WebkitTouchCallout: "none",
              py: 4,
            }}
          >
            <Box sx={{ textAlign: "left" }}>{children}</Box>
          </Container>
        </Box>

        <Footer />
      </Box>
    </ThemeProvider>
  );
}
